import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useGetUserDropdownOptionsQuery } from '../gql/_gen_/report-subscriptions.gql';

type Props = {
  value?: number;
  onChange: (nextUserId: number) => void;
  disabled?: boolean;
};

export function RmUserSelect({ value, onChange, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, loading, error } = useGetUserDropdownOptionsQuery();

  const options = data?.userDropdownOptions ?? [];

  const selected = useMemo(() => options.find((u) => u?.id === value) ?? null, [options, value]);

  const filteredOptions = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return options;

    return options.filter((u) => {
      const name = (u?.name ?? '').toLowerCase();
      const email = (u?.email ?? '').toLowerCase();
      return name.includes(normalized) || email.includes(normalized);
    });
  }, [options, search]);

  const selectedLabel = selected?.name || selected?.email || (value ? `User ${value}` : '');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between'
          disabled={disabled}
        >
          <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel || 'Select a revenue manager'}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[--radix-popover-trigger-width] p-2' align='start'>
        <div className='space-y-2'>
          <Input
            placeholder='Search users...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />

          {loading && (
            <div className='flex items-center gap-2 text-sm text-muted-foreground px-2 py-1'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading users...
            </div>
          )}

          {!loading && error && (
            <div className='text-sm text-red-500 px-2 py-1'>Failed to load users</div>
          )}

          {!loading && !error && (
            <ScrollArea className='h-56'>
              <div className='space-y-1'>
                {filteredOptions.length === 0 ? (
                  <div className='text-sm text-muted-foreground px-2 py-1'>No users found</div>
                ) : (
                  filteredOptions.map((u) => (
                    <button
                      key={u.id}
                      type='button'
                      className={cn(
                        'w-full text-left flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                        u.id === value && 'bg-accent text-accent-foreground',
                      )}
                      onClick={() => {
                        onChange(u.id);
                        setOpen(false);
                      }}
                      disabled={disabled}
                    >
                      <Check
                        className={cn('h-4 w-4', u.id === value ? 'opacity-100' : 'opacity-0')}
                      />
                      <div className='min-w-0'>
                        <div className='truncate'>{u.name || u.email || `User ${u.id}`}</div>
                        {u.name && u.email && (
                          <div className='truncate text-xs text-muted-foreground'>{u.email}</div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
